<template>
  <div class="w-full">
    <div v-if="layers.length > 0">
      <div class="flex items-center gap-2 bg-[#eef3fb] border border-[#c9d6ef] text-[#2f5aad] text-xs rounded-lg px-3 py-2 mb-3" role="alert">
        <b-icon pack="fas" icon="circle-info" size="is-small" class="flex-shrink-0" />
        <p class="leading-snug">{{ $t('layers_design_hint') }}</p>
      </div>

      <b-collapse v-for="(layer, index) of layers"
        :ref="'collapse_lyr_' + index"
        class="card mb-1"
        :class="{'border': isOpen == index, 'border-blue': isOpen == index, 'rounded': isOpen == index}"
        animation="slide"
        :key="'tab_lyr_' + index"
        :open="isOpen == index"
        @open="isOpen = index"
        :aria-id="'contentIdForA11y5-' + index">
        <template #trigger="props">
          <div
            class="card-header"
            role="button"
            :aria-controls="'contentIdForA11y5-' + index"
            :style="_getStyle(props.open, layer)"
            :aria-expanded="props.open">
            <p class="card-header-title items-center gap-2" :style="{ color: props.open ? '#ffffff !important' : 'hsl(0, 0%, 21%)'}">
              <span class="inline-flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0"
                :style="{ backgroundColor: _GetCssColor(layer.color) }">
                <b-icon pack="fas" icon="layer-group" size="is-small"></b-icon>
              </span>
              <!-- Dos filas compactas: nombre (elipsis) + stats en gris. -->
              <span class="min-w-0 flex-1 leading-tight">
                <span class="block truncate font-medium mb-0">{{ layer.name }}</span>
                <!-- Fila de stats: solo con el collapse CERRADO (al abrir se oculta). -->
                <span v-if="!props.open" class="block text-[11px] font-normal text-gray-400 truncate">
                  {{ $t('structures') }}: {{ layerStats[index].structures }} · {{ $t('axles') }}: {{ layerStats[index].axles }}<template v-if="createElements.pilings"> · {{ $t('pilings') }}: {{ layerStats[index].pilings }}</template><template v-if="createElements.modules"> · {{ $t('modules') }}: {{ layerStats[index].modules }} · {{ $t('panels') }}: {{ layerStats[index].panels }}</template>
                </span>
              </span>
            </p>
            <a class="card-header-icon">
              <b-icon
                  :icon="props.open ? 'angle-down' : 'angle-up'" pack="fas">
              </b-icon>
            </a>
          </div>
        </template>

        <div class="card-content">
          <div class="content-">
            <fields-design-form :ref="'frm_lys_' + index"
              :disable-fields="disableFields"
              :create-elements="createElements"
              :structure-count="_LayerCount(layer)"
              compact
              @epc-change="(f) => _OnLayerFormChange(index, f)"
            />
          </div>
        </div>
        <footer class="card-footer">
          <b-button
            :label="$t('validate')"
            icon-pack="fas"
            icon-left="check"
            expanded
            class="!bg-[#3a6bc8] hover:!bg-[#2f5aad] !text-white !border-0"
            @click="_HandleValidate( layer, index)"/>

          <!-- <a class="card-footer-item p-2" @click="_HandleOpenModal( { type: layer.code, status: null } )">
            <b-icon pack="fas" icon="plus-circle"></b-icon> {{ $t('add') }}
          </a> -->
          <!-- <a class="card-footer-item" @click="_OnSave"> <b-icon pack="fas" icon="save"></b-icon> {{ $t('save') }}</a> -->
          <!-- <a class="card-footer-item">Delete</a> -->
        </footer>
      </b-collapse>
    </div>
    <div v-else class="flex flex-row bg-grey-light border hover:border-blue rounded">
      <p class="text-center w-full">El diseño no contiene capas</p>
    </div>
  </div>
</template>

<script>
import FieldsDesignForm from "./fields-design-form.vue"

export default {
  name: "layers-design-list",

  components: {
    FieldsDesignForm
  },

  props: {
    layers: { type: Array, required: true },
    disableFields: { type: Boolean, default: false },
    createElements: { type: Object, required: true },
    // [{ layer, count }] — nº de estructuras (DXF) por capa, para mostrar el total
    // de la capa y el conteo resultante bajo cada input del formulario.
    structuresByLayer: { type: Array, default: () => [] },
    // Config previa por capa (rehidratación): { [nombreCapaSaneado]: design }.
    // Siembra dataDesign + cada form al montar, para no perder lo ya configurado.
    initialDesign: { type: Object, default: () => ({}) },
  },

  data: () => ({

    dataDesign: {}, // contendrá la cantidad de hincados, modulos y paneles por capas a ser diseñado
    isOpen: -1, // para menejar que panel esta colapsado o activo
    // Valores actuales del formulario de cada capa (index → form) para las stats
    // de la cabecera. Lo alimenta cada fields-design-form vía @epc-change.
    layerForms: {},
  }),

  created() {
  },

  mounted() {
    this._SeedFromInitial()
  },

  computed: {
    /** Stats por capa (alineadas a `layers` por índice) para la cabecera del collapse. */
    layerStats() {
      return this.layers.map((lyr, index) => this._LayerStats(lyr, index))
    },
  },

  methods: {

    getDataDesign() {
      return this.dataDesign
    },

    /** Siembra dataDesign + cada form de capa con la config previa (initialDesign). */
    _SeedFromInitial() {
      const init = this.initialDesign || {}
      if (!Object.keys(init).length) return
      this.dataDesign = { ...init }
      this.$nextTick(() => {
        this.layers.forEach((lyr, index) => {
          const cfg = init[lyr.name.replace(/\s/g, '_')]
          const ref = this.$refs['frm_lys_' + index]
          if (cfg && ref && ref[0]) ref[0].setForm(cfg)
        })
        // Avisa al padre la config sembrada (sincroniza su resumen).
        this.$emit('epc-change', { ...this.dataDesign })
      })
    },

    /** Nº de estructuras (DXF) de una capa, por nombre. */
    _LayerCount(lyr) {
      const found = this.structuresByLayer.find(r => r.layer === lyr.name)
      return found ? found.count : 0
    },

    /** Guarda los valores del form de una capa (para las stats de la cabecera). */
    _OnLayerFormChange(index, form) {
      this.$set(this.layerForms, index, { ...form })
    },

    /** Stats de una capa = estructuras (DXF) × config del form de esa capa. */
    _LayerStats(lyr, index) {
      const s = this._LayerCount(lyr)
      const f = this.layerForms[index] || {}
      const modules = this.createElements.modules ? s * (Number(f.total_modules) || 0) : 0
      return {
        structures: s,
        axles:      s * (Number(f.total_axles) || 0),
        // Hincados = estructuras × ejes × hincados por eje.
        pilings:    this.createElements.pilings ? s * (Number(f.total_axles) || 0) * (Number(f.total_pilings) || 0) : 0,
        modules,
        panels:     this.createElements.modules ? modules * (Number(f.total_panels) || 0) : 0,
      }
    },

    _getStyle(open, lyr) {
      let color = 'initial'
      if( this.dataDesign.hasOwnProperty(lyr.name.replace(/\s/g, '_')) ) {
        color = '#C5E1A5'
      }

      return { backgroundColor: open ? '#3a6bc8' : color}
    },

    _HandleValidate(lyr, refindex) {
      let dataFrm = this.$refs['frm_lys_' + refindex][0].getDataForm()

      if (dataFrm != null) {
        this.$set(this.dataDesign, lyr.name.replace(/\s/g, '_'), dataFrm)
        // Avisamos al padre con la config validada por capa (para el resumen).
        this.$emit('epc-change', { ...this.dataDesign })
      }

      this.$refs['collapse_lyr_' + refindex][0].toggle()
    },

    _GetCssColor(value) {
      let s = value.toString(16)
      while (s.length < 6) {
          s = "0" + s
      }

      if (s=="ffffff") // TODO: cuando es blanco tratamos tratar aquí de momento mandamos gris bajo al 10% si es necesario o mostrar un borde y dejar fondo blanco
        s = 'f2f2f2'
      return "#" + s
    }
  },
};
</script>
